import React, { useState } from 'react';

export default function EmailMarketing() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [interests, setInterests] = useState([]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showUnsubscribeConfirm, setShowUnsubscribeConfirm] = useState(false);

  const interestOptions = [
    'Solo Travel',
    'Group Travel',
    'Adventure Travel',
    'Budget Travel',
    'Luxury Travel',
    'Cultural Travel',
    'Food & Wine Travel',
    'Photography Travel',
    'Business Travel',
    'Family Travel'
  ];

  const handleInterestToggle = (interest) => {
    setInterests(prev => 
      prev.includes(interest) 
        ? prev.filter(i => i !== interest)
        : [...prev, interest]
    );
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error('Please enter a valid email address.');
      }

      // Simulate API call to subscribe
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Store subscription in localStorage
      const subscription = {
        email,
        name,
        interests,
        subscribedAt: new Date().toISOString(),
        source: 'gradedwash.com'
      };
      
      localStorage.setItem('emailSubscription', JSON.stringify(subscription));
      
      // Send welcome email (simulated)
      console.log('Sending welcome email to:', email);
      
      setIsSubscribed(true);
      setEmail('');
      setName('');
      setInterests([]);
    } catch (error) {
      console.error('Subscription error:', error);
      alert(error.message || 'Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    setLoading(true);
    
    try {
      // Simulate API call to unsubscribe
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      localStorage.removeItem('emailSubscription');
      setIsSubscribed(false);
      setShowUnsubscribeConfirm(true);
    } catch (error) {
      console.error('Unsubscribe error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (isSubscribed) {
    return (
      <div className="email-success">
        <div className="success-icon">✅</div>
        <h3>Welcome to GradedWash!</h3>
        <p>Thank you for subscribing to our travel companion newsletter. You'll receive:</p>
        <ul>
          <li>🎯 Personalized travel companion matches</li>
          <li>📧 Travel safety tips and advice</li>
          <li>✈️ New travel group announcements</li>
          <li>🌍 Destination guides and travel stories</li>
          <li>💡 Exclusive travel deals and offers</li>
        </ul>
        <p className="email-note">
          Emails will be sent from <strong>gradedwash@gmail.com</strong>
        </p>
        <button 
          className="btn btn-secondary"
          onClick={handleUnsubscribe}
          disabled={loading}
        >
          {loading ? 'Unsubscribing...' : 'Unsubscribe'}
        </button>
      </div>
    );
  }

  return (
    <div className="email-marketing">
      <div className="email-header">
        <h3>🌍 Stay Connected with GradedWash</h3>
        <p>Get personalized travel companion matches, safety tips, and exclusive travel content delivered to your inbox.</p>
      </div>

      <form onSubmit={handleSubscribe} className="email-form">
        <div className="form-group">
          <label htmlFor="name">Name</label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your.email@example.com"
            required
          />
          <small>We'll send emails from gradedwash@gmail.com</small>
        </div>

        <div className="form-group">
          <label>Travel Interests (Select all that apply)</label>
          <div className="interests-grid">
            {interestOptions.map(interest => (
              <label key={interest} className="interest-checkbox">
                <input
                  type="checkbox"
                  checked={interests.includes(interest)}
                  onChange={() => handleInterestToggle(interest)}
                />
                <span>{interest}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="email-benefits">
          <h4>What you'll receive:</h4>
          <ul>
            <li>🎯 <strong>Personalized Matches:</strong> Travel companions based on your personality</li>
            <li>📧 <strong>Safety Tips:</strong> Essential travel safety advice and guidelines</li>
            <li>✈️ <strong>Travel Groups:</strong> New group travel opportunities</li>
            <li>🌍 <strong>Destination Guides:</strong> Travel stories and tips from our community</li>
            <li>💡 <strong>Exclusive Offers:</strong> Special deals from our travel partners</li>
          </ul>
        </div>

        <div className="privacy-note">
          <p>
            By subscribing, you agree to receive emails from GradedWash. 
            We respect your privacy and will never share your email with third parties. 
            You can unsubscribe at any time.
          </p>
        </div>

        <button 
          type="submit" 
          className="btn btn-primary btn-lg"
          disabled={loading || !email || !name}
        >
          {loading ? 'Subscribing...' : 'Subscribe to Travel Updates'}
        </button>
      </form>
      
      {/* Unsubscribe Confirmation Popup */}
      {showUnsubscribeConfirm && (
        <div className="unsubscribe-popup-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="unsubscribe-popup" style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
            <h3 style={{ 
              color: '#333', 
              marginBottom: '15px', 
              fontSize: '24px',
              fontWeight: 'bold'
            }}>
              Thank you!
            </h3>
            <p style={{ 
              color: '#666', 
              lineHeight: '1.6', 
              marginBottom: '25px',
              fontSize: '16px'
            }}>
              You are now unsubscribed from all travel newsletters.
            </p>
            <p style={{ 
              color: '#888', 
              fontSize: '14px',
              marginBottom: '25px'
            }}>
              Come back anytime to rejoin our travel community!
            </p>
            <button 
              onClick={() => setShowUnsubscribeConfirm(false)}
              style={{
                backgroundColor: '#667eea',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#5a67d8'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#667eea'}
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Email marketing utilities
export const sendWelcomeEmail = async (email, name) => {
  // This would integrate with your email service (Mailchimp, SendGrid, etc.)
  console.log('Sending welcome email to:', email, name);
  
  const emailData = {
    to: email,
    from: 'gradedwash@gmail.com',
    subject: 'Welcome to GradedWash - Start Your Travel Journey!',
    template: 'welcome-email',
    data: {
      name,
      signupDate: new Date().toLocaleDateString(),
      website: 'https://gradedwash.com'
    }
  };
  
  // Simulate email sending
  return new Promise(resolve => setTimeout(() => resolve(emailData), 1000));
};

export const sendTravelUpdate = async (subscribers, content) => {
  // Send travel updates to all subscribers
  console.log('Sending travel update to', subscribers.length, 'subscribers');
  
  const emailData = {
    from: 'gradedwash@gmail.com',
    subject: content.subject,
    template: 'travel-update',
    data: content
  };
  
  // Simulate bulk email sending
  return new Promise(resolve => setTimeout(() => resolve(emailData), 2000));
};

export const getSubscriberStats = () => {
  // Get email marketing statistics
  const subscription = localStorage.getItem('emailSubscription');
  return subscription ? JSON.parse(subscription) : null;
}; 